import { IconBell, IconCheck, IconChevronRight, IconInfoTriangle, IconLogout, IconMoon, IconSun } from '@tabler/icons-react';
import React, { useState } from 'react';
import { useSideNavAPI } from '../API/sideNavAPI';
import { ButtonIconSquare, ButtonRounded } from './buttons';
import { useDarkmodeAPI } from '../API/darkmodeAPI';
import { useAuthenticationHook } from 'src/API/rest.authentication';
import { AvatarSection } from './user_section';
import { useTaskStatus, useMarkTaskCompleted, Notification } from 'src/API/rest.taskStatus';

// Confirmation Modal
const ConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  action: string;
}> = ({ isOpen, onClose, onConfirm, action }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-950 p-6 rounded-lg shadow-lg w-full max-w-sm">
        <h2 className="text-lg font-bold mb-4 text-gray-900 dark:text-gray-100">
          Confirm Task Completion
        </h2>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
          Have you completed: <strong>{action}</strong>?
        </p>
        <div className="flex gap-4">
          <button onClick={onConfirm} className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-500">
            Yes, Completed
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-300 dark:bg-gray-700 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-600">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Notification Modal
const NotificationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkComplete: (id: number, employee_id: number) => void;
  isMarking: boolean;
}> = ({ isOpen, onClose, notifications, onMarkComplete, isMarking }) => {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<{ id: number; employee_id: number; action: string } | null>(null);

  const handleMarkClick = (id: number, employee_id: number, action: string) => {
    setSelectedTask({ id, employee_id, action });
    setConfirmationOpen(true);
  };

  const handleConfirm = () => {
    if (selectedTask) onMarkComplete(selectedTask.id, selectedTask.employee_id);
    setConfirmationOpen(false);
    setSelectedTask(null);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-950 p-6 rounded-lg shadow-xl w-full max-w-lg mx-4">
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <IconBell /> Notifications ({notifications.length})
          </h2>

          {notifications.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No pending tasks 🎉</p>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-3">
              {notifications.map((n) => (
                <div
                  key={n.FT08_ForecastCalenderResponsibilityID}
                  className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-900 relative group hover:shadow-md transition"
                >
                  <p className="font-semibold">{n.FT06_action}</p>
                  <p className={`text-sm font-medium ${n.FT08_is_completed ? 'text-green-600' : 'text-red-600'}`}>
                    Status: {n.FT08_is_completed ? 'Completed' : 'Pending'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Date: {n.FT08_T07_ForecastCalenderline.FT07_Date} → Target: {n.FT08_T07_ForecastCalenderline.FT07_TargetDate}
                  </p>

                  {!n.FT08_is_completed && (
                    <button
                      onClick={() => handleMarkClick(n.FT08_ForecastCalenderResponsibilityID, n.FT08_M03_EmployeeID, n.FT06_action)}
                      disabled={isMarking}
                      className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-green-700 to-green-400/20 text-white text-lg font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300"
                    >
                      {isMarking ? 'Saving...' : 'Mark as Completed'} <IconCheck className="ml-2" size={20} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <button onClick={onClose} className="mt-6 w-full bg-red-900 text-white py-3 rounded-lg hover:bg-red-800">
            Close
          </button>
        </div>
      </div>

      <ConfirmationModal isOpen={confirmationOpen} onClose={() => setConfirmationOpen(false)} onConfirm={handleConfirm} action={selectedTask?.action || ''} />
    </>
  );
};

export default function NavBar() {
  const { showSideNav } = useSideNavAPI();
  const { logout } = useAuthenticationHook();
  const { mode, toggleDarkMode } = useDarkmodeAPI();
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  const { data: notificationResponse, isLoading } = useTaskStatus();
  const { mutate: markTaskCompleted, isPending: isMarking } = useMarkTaskCompleted();

  const notifications = notificationResponse?.status === 'success' ? notificationResponse.data : [];
  const pendingCount = notifications.filter(n => !n.FT08_is_completed).length;

  const toggleNotification = () => setIsNotificationModalOpen(prev => !prev);
  const handleMarkComplete = (id: number, employee_id: number) => markTaskCompleted({ id, employee_id });

  // Logout with confirmation
  const LogoutWithConfirm = () => {
    const [open, setOpen] = useState(false);
    return (
      <>
        {/* Use title attribute instead of prop if ButtonRounded doesn't accept it */}
        <div title="Logout">
          <ButtonRounded onClick={() => setOpen(true)} icon={<IconLogout size={15} />} />
        </div>

        {open && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-950 p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <IconInfoTriangle /> Confirm Logout
              </h2>
              <p className="text-sm mb-6">Are you sure you want to log out?</p>
              <div className="flex gap-3">
                <button onClick={() => { logout(); setOpen(false); }} className="flex-1 bg-red-900 text-white py-2 rounded hover:bg-red-800">
                  Yes, Logout
                </button>
                <button onClick={() => setOpen(false)} className="flex-1 bg-gray-300 dark:bg-gray-700 py-2 rounded hover:bg-gray-400 dark:hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <nav className="grow h-[100px] max-h-[70px] min-h-[70px] flex items-center px-2 justify-between">
        <div className="p-2 sm:hidden">
          <ButtonIconSquare icon={<IconChevronRight />} onClick={showSideNav} />
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-3">
          <LogoutWithConfirm />

          {/* Dark mode button with tooltip */}
          <div title={mode === 'light' ? 'Enable Dark Mode' : 'Enable Light Mode'}>
            <ButtonRounded onClick={toggleDarkMode} icon={mode === 'dark' ? <IconMoon size={15} /> : <IconSun size={15} />} />
          </div>

          <div className="relative">
            <div title="Notifications">
              <ButtonRounded
                onClick={toggleNotification}
                icon={isLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <IconBell size={15} />}
              />
            </div>
            {pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {pendingCount}
              </span>
            )}
          </div>

          <AvatarSection />
        </div>
      </nav>

      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={toggleNotification}
        notifications={notifications}
        onMarkComplete={handleMarkComplete}
        isMarking={isMarking}
      />
    </>
  );
}